import axios from 'axios';
import { API_CONFIG } from '@/config/api.config';
import type {
  AccessLevel,
  ActionChat,
  ActionChatMessage,
  ActionClaim,
  ActionMatter,
  ActionSignature,
  ApplicationSummary,
  ArgumentOutline,
  ArtOfRecord,
  CitedReference,
  ClientEmail,
  ClientEmailVersion,
  ReportingRun,
  IssueAnalysis,
  FilledShell,
  Issue,
  IssueResponse,
  LetterSection,
  MatterBundle,
  NplSearch,
  ProsecutionDocument,
  ProsecutionTimeline,
  RemarkSection,
  ReportingMessage,
  ReportingTemplate,
  ResponseShell,
  ResponseTemplate,
  Share,
  SharedMatter,
  TemplateLibrary,
  Strategy,
} from './types';

/**
 * /action API client.
 *
 * Points at the /action blueprint on the existing PatentLoop backend, so it inherits the
 * CORS config that already works for this app — no separate API domain and no separate
 * preflight rules.
 *
 * Plain JSON with a Bearer token, like the drafting and docket clients and unlike the
 * main API's AES envelope: this is REST CRUD where GET/POST/PUT/PATCH/DELETE are the
 * natural verbs. The token is the same `id_token` the app already keeps in localStorage,
 * kept fresh by useSessionRefresh, so there is no extra session handling.
 */
const api = axios.create({
  baseURL: `${API_CONFIG.baseUrl}/action`,
  // Generation is enqueued rather than awaited, so most requests here stay short. The
  // ones that read from the USPTO override this.
  timeout: 30000,
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('id_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/** A generation step returns a job id; the caller then watches the matter's status. */
export interface JobHandle {
  jobId: string;
}

export const actionApi = {
  // ---- matters

  /** This month's count against the plan's ceiling. */
  async getQuota(): Promise<MonthlyQuota> {
    return (await api.get('/quota')).data;
  },

  /**
   * Your matters, and the ones shared with you, kept apart.
   *
   * Two lists rather than one for the same reason the endpoint returns two: a borrowed
   * matter is not yours to delete or to share onward, and a list that mixed them without
   * saying which is which is a list nobody can act on safely.
   */
  async listMatters(): Promise<{ matters: ActionMatter[]; shared: SharedMatter[] }> {
    const { data } = await api.get('/matters');
    return { matters: data.matters ?? [], shared: data.shared ?? [] };
  },

  async createMatter(input: {
    applicationNumber?: string;
    title?: string;
  }): Promise<ActionMatter> {
    return (await api.post('/matters', input)).data.matter;
  },

  /** The whole matter in one read — every step renders from this. */
  async getMatter(matterId: string): Promise<MatterBundle> {
    return (await api.get(`/matters/${matterId}`)).data;
  },

  /** Cheap poll target while a generation step runs. */
  async getStatus(
    matterId: string
  ): Promise<{ status: string; step: string; error: string }> {
    return (await api.get(`/matters/${matterId}/status`)).data;
  },

  async updateMatter(
    matterId: string,
    fields: {
      title?: string;
      applicationNumber?: string;
      officeActionDocId?: string;
      officeActionDate?: string;
      responseDueDate?: string;
    }
  ): Promise<ActionMatter> {
    return (await api.patch(`/matters/${matterId}`, fields)).data.matter;
  },

  async deleteMatter(matterId: string): Promise<void> {
    await api.delete(`/matters/${matterId}`);
  },

  // ---- sharing

  /** Who this matter is shared with, and what you may do with it. */
  async listShares(matterId: string): Promise<{
    owner: string;
    yourAccess: AccessLevel | 'owner';
    shares: Share[];
  }> {
    const { data } = await api.get(`/matters/${matterId}/shares`);
    return { owner: data.owner, yourAccess: data.yourAccess, shares: data.shares ?? [] };
  },

  /**
   * Share it with someone. Returns a `warning` when the username does not look like a
   * real account — the share is saved either way, because there is no user directory to
   * check against and refusing on a guess would be wrong, but a typo that silently shares
   * with nobody is worth saying out loud.
   */
  async share(
    matterId: string,
    username: string,
    level: AccessLevel
  ): Promise<{ share: Share; warning: string }> {
    const { data } = await api.post(`/matters/${matterId}/shares`, { username, level });
    return { share: data.share, warning: data.warning ?? '' };
  },

  async unshare(matterId: string, recipient: string): Promise<void> {
    await api.delete(`/matters/${matterId}/shares/${encodeURIComponent(recipient)}`);
  },

  /** Advance the flow. The only call that moves `step`. */
  async confirmStep(matterId: string, step: string): Promise<ActionMatter> {
    return (await api.post(`/matters/${matterId}/confirm`, { step })).data.matter;
  },

  // ---- step 1: the application and its prosecution history

  /**
   * Fetch an application and its file wrapper from the USPTO.
   *
   * Slower than the rest: it reaches the USPTO and loads the prosecution documents, so
   * the timeout is raised well past the default.
   */
  async attachApplication(
    matterId: string,
    applicationNumber: string
  ): Promise<{
    matter: ActionMatter;
    documents: ProsecutionDocument[];
    application: ApplicationSummary;
  }> {
    return (
      await api.post(
        `/matters/${matterId}/application`,
        { applicationNumber },
        { timeout: 180000 }
      )
    ).data;
  },

  /**
   * The application's data and its file wrapper.
   *
   * Served from what was stored when the application was attached, so any step can show
   * it without a USPTO round trip. `refresh` goes back to the Office, which is what you
   * want after a new paper is entered and not otherwise.
   */
  async getApplication(
    matterId: string,
    refresh = false
  ): Promise<{ application: ApplicationSummary; documents: ProsecutionDocument[] }> {
    return (
      await api.get(`/matters/${matterId}/application`, {
        params: refresh ? { refresh: 1 } : undefined,
        timeout: refresh ? 180000 : 30000,
      })
    ).data;
  },

  /** The text of one prosecution document, read on demand rather than stored. */
  async getDocumentText(
    matterId: string,
    docId: string
  ): Promise<{ docId: string; text: string; url: string }> {
    return (
      await api.get(`/matters/${matterId}/documents/${docId}/text`, { timeout: 120000 })
    ).data;
  },

  /**
   * Attach a document that is not in the file wrapper.
   *
   * An Office action arrives by post before it appears in PAIR. The text comes back
   * rather than being stored: the caller decides what it is and posts it to the step
   * that needs it.
   */
  async uploadDocument(
    matterId: string,
    file: File,
    /** What the document is. Naming it keeps the text server-side, so the step that
     *  reads it later can find it — the upload and the reading are different screens. */
    role?: 'office_action' | 'claims'
  ): Promise<{
    filename: string;
    /** Always empty now. Neither paper is read inside the request — an Office action is
     *  OCR'd by the worker, and its text and caption reach the matter when that
     *  finishes. Watch `matter.uploadedAction.pending`. */
    text: string;
    /** Set when an Office action was queued for reading. */
    jobId?: string;
    reading?: boolean;
    truncated: boolean;
    textKey?: string;
    /** The stored file. The claims step passes it back so the worker can render the
     *  pages and read which words are struck through — extraction loses that. */
    sourceKey?: string;
    /** For an Office action: what its caption says about the case. */
    caption?: Record<string, string>;
  }> {
    const form = new FormData();
    form.append('file', file);
    if (role) form.append('role', role);
    return (
      await api.post(`/matters/${matterId}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      })
    ).data;
  },

  // ---- step 3: the rejections

  async generateIssues(
    matterId: string,
    input: { docId?: string; actionText?: string; instructions?: string }
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/issues/generate`, input)).data;
  },

  async saveIssues(matterId: string, issues: Issue[]): Promise<Issue[]> {
    return (await api.put(`/matters/${matterId}/issues`, { issues })).data.issues;
  },

  async updateIssue(
    matterId: string,
    order: number,
    fields: Partial<Issue>
  ): Promise<Issue> {
    return (await api.patch(`/matters/${matterId}/issues/${order}`, fields)).data.issue;
  },

  /**
   * The sections a letter to the client is written in — the firm's own where they have
   * set any, the defaults otherwise.
   */
  async getLetterTemplate(): Promise<{
    sections: LetterSection[];
    defaults: LetterSection[];
    isDefault: boolean;
    forbidden: string[];
  }> {
    return (await api.get('/letter/template')).data;
  },

  /** Replace the template. Sending none restores the defaults. */
  async saveLetterTemplate(
    sections: LetterSection[]
  ): Promise<{ sections: LetterSection[]; isDefault: boolean }> {
    return (await api.put('/letter/template', { sections })).data;
  },

  // ---- downloads
  //
  // What the matter is built on, in a form the attorney can keep: the action, the claims
  // and the art.

  /** One prosecution document — the PDF of record where it can be had. */
  // ---- the matter's chat
  //
  // Questions about this matter, answered from everything it holds: the Office action,
  // the rejections, the claims of record, the cited art, the specification, the file
  // wrapper and whatever has been drafted. Read-only — see `ActionChat`.

  async listChats(matterId: string): Promise<ActionChat[]> {
    return (await api.get(`/matters/${matterId}/chats`)).data.chats;
  },

  /** Open a thread, optionally with its first question. */
  async createChat(
    matterId: string,
    question?: string,
    options: { model?: string; length?: string } = {}
  ): Promise<{ chat: ActionChat; messages: ActionChatMessage[]; jobId?: string }> {
    return (await api.post(`/matters/${matterId}/chats`, { question, ...options })).data;
  },

  /** One thread with its turns. Polled while an answer is pending. */
  async getChat(
    matterId: string,
    chatId: string
  ): Promise<{ chat: ActionChat; messages: ActionChatMessage[] }> {
    return (await api.get(`/matters/${matterId}/chats/${chatId}`)).data;
  },

  /** Ask the next question. The answer lands on the pending turn — poll `getChat`. */
  async sendChatMessage(
    matterId: string,
    chatId: string,
    question: string,
    /** Chosen per turn, and validated server-side against what this account may run. */
    options: { model?: string; length?: string } = {}
  ): Promise<{ chat: ActionChat; messages: ActionChatMessage[]; jobId?: string }> {
    return (
      await api.post(`/matters/${matterId}/chats/${chatId}/messages`, { question, ...options })
    ).data;
  },

  async deleteChat(matterId: string, chatId: string): Promise<void> {
    await api.delete(`/matters/${matterId}/chats/${chatId}`);
  },

  /**
   * Accept, reject, or take back the decision on one change the chat proposed.
   *
   * Accepting writes it into the matter — the same write the page for that thing makes —
   * and returns what changed, in words, for the page to say so. Undoing an acceptance
   * puts back what it replaced; undoing a rejection just makes it available again.
   */
  async decideProposal(
    matterId: string,
    chatId: string,
    messageId: string,
    proposalId: string,
    decision: 'accept' | 'reject' | 'undo'
  ): Promise<{ message: ActionChatMessage; applied: string }> {
    return (
      await api.post(
        `/matters/${matterId}/chats/${chatId}/messages/${messageId}/proposals/${proposalId}`,
        { decision }
      )
    ).data;
  },

  /** Decide several of one answer's proposals at once. */
  async decideProposals(
    matterId: string,
    chatId: string,
    messageId: string,
    decision: 'accept' | 'reject' | 'undo',
    ids?: string[]
  ): Promise<{ message: ActionChatMessage; applied: string[]; refused: string[] }> {
    return (
      await api.post(
        `/matters/${matterId}/chats/${chatId}/messages/${messageId}/proposals`,
        { decision, ...(ids ? { ids } : {}) }
      )
    ).data;
  },

  async downloadDocument(
    matterId: string,
    docId: string,
    filename?: string
  ): Promise<void> {
    await download(`/matters/${matterId}/documents/${docId}/download`, filename);
  },

  /** The claims as they now stand, as a 1.121(c) listing. */
  async downloadClaims(matterId: string): Promise<void> {
    await download(`/matters/${matterId}/claims/download`);
  },

  /**
   * One piece of cited art, as text or as the published PDF.
   *
   * Text is what the analysis reads and is always there once retrieval succeeded. The
   * PDF is the document as published — what you read, and the only form in which the
   * figures exist.
   */
  async downloadReference(
    matterId: string,
    referenceKey: string,
    format: 'text' | 'pdf' = 'text'
  ): Promise<void> {
    const query = format === 'pdf' ? '?format=pdf' : '';
    await download(`/matters/${matterId}/references/${referenceKey}/download${query}`);
  },

  /**
   * One document cited in a prosecution timeline, as published.
   *
   * Fetched by number rather than through a matter: the timeline is offered on an
   * application nobody has opened a response for, and it is the same document either way.
   * Only art the reading found a number for can be asked for — a reference named by its
   * author alone has none, and the page offers no download for it.
   */
  async downloadArt(art: ArtOfRecord): Promise<void> {
    if (!art.number) throw new Error(`${art.reference} was cited without a document number`);
    const query = new URLSearchParams({ number: art.number });
    if (art.kind) query.set('kind', art.kind);
    if (art.reference) query.set('name', art.reference);
    await download(`/art/download?${query.toString()}`, `${art.reference}.pdf`);
  },

  /** Every retrieved reference, as a zip of text or of PDFs. */
  async downloadAllReferences(
    matterId: string,
    format: 'text' | 'pdf' = 'text'
  ): Promise<void> {
    const query = format === 'pdf' ? '?format=pdf' : '';
    await download(`/matters/${matterId}/references/download${query}`);
  },

  // ---- the letter to the client
  //
  // Distinct from the reporting message: that one says an action arrived, this one says
  // what is proposed about it and asks the client to agree.

  /**
   * Write the letter. `instructions` is what to do differently this time — "shorter",
   * "do not mention the interview", "explain the entropy limitation in plain terms".
   */
  async generateClientEmail(
    matterId: string,
    input: { instructions?: string } = {}
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/client-email/generate`, input)).data;
  },

  async getClientEmail(matterId: string): Promise<ClientEmail | null> {
    return (await api.get(`/matters/${matterId}/client-email`)).data.email;
  },

  /** Save an edited letter. It goes out over the attorney's name, so it is theirs. */
  async saveClientEmail(
    matterId: string,
    input: { subject?: string; body: string }
  ): Promise<ClientEmail> {
    return (await api.put(`/matters/${matterId}/client-email`, input)).data.email;
  },

  /** Earlier versions of the letter, newest first — enough of each to choose between. */
  async listClientEmailHistory(matterId: string): Promise<ClientEmailVersion[]> {
    return (await api.get(`/matters/${matterId}/client-email/history`)).data.versions ?? [];
  },

  /** One earlier version, whole. */
  async getClientEmailVersion(matterId: string, ranAt: string): Promise<ClientEmail> {
    return (
      await api.get(`/matters/${matterId}/client-email/history/${encodeURIComponent(ranAt)}`)
    ).data.email;
  },

  /** Make an earlier version current. What it replaces is archived by the same write. */
  async restoreClientEmailVersion(matterId: string, ranAt: string): Promise<ClientEmail> {
    return (
      await api.post(
        `/matters/${matterId}/client-email/history/${encodeURIComponent(ranAt)}/restore`
      )
    ).data.email;
  },

  // ---- the reporting message
  //
  // Run through the same task the Tasks page runs. A firm's reporting message is a house
  // style, and two tools writing it differently is worse than one tool writing it
  // somewhere the attorney has to go and find.

  async listReportingTemplates(): Promise<{
    templates: ReportingTemplate[];
    default: string;
  }> {
    const { data } = await api.get('/reporting/templates');
    // The server names its own default; falling back to the first entry keeps an older
    // server working rather than hard-coding a second opinion about which one it is.
    return {
      templates: data.templates ?? [],
      default: data.default || data.templates?.[0]?.id || 'brief',
    };
  },

  /** Write the message reporting this action. Enqueued; poll `getReportingMessage`. */
  async generateReportingMessage(
    matterId: string,
    input: { template?: string; docId?: string; actionType?: string } = {}
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/reporting/generate`, input)).data;
  },

  async getReportingMessage(
    matterId: string,
    jobId?: string
  ): Promise<ReportingMessage> {
    return (
      await api.get(`/matters/${matterId}/reporting`, {
        params: jobId ? { jobId } : undefined,
      })
    ).data;
  },

  /** Everything written or rewritten for this matter, newest first. */
  async listReportingHistory(matterId: string): Promise<ReportingRun[]> {
    return (await api.get(`/matters/${matterId}/reporting/history`)).data.runs ?? [];
  },

  /**
   * Save your own version of the reporting message.
   *
   * Kept beside the run it was made from rather than over it, so what the model wrote
   * stays readable and can be gone back to.
   */
  async saveReportingMessage(
    matterId: string,
    input: { message: string; jobId?: string }
  ): Promise<ReportingMessage> {
    return (await api.put(`/matters/${matterId}/reporting`, input)).data;
  },

  /** Drop your editing and go back to what was written. The edit stays in the history. */
  async clearReportingEdit(matterId: string): Promise<void> {
    await api.delete(`/matters/${matterId}/reporting/edit`);
  },

  /** One earlier version of your own editing, whole. */
  async getReportingVersion(matterId: string, ranAt: string): Promise<ReportingMessage> {
    return (
      await api.get(`/matters/${matterId}/reporting/version/${encodeURIComponent(ranAt)}`)
    ).data;
  },

  // ---- the cited art

  /** The art the rejections rely on, and whether it could be retrieved. */
  async listReferences(matterId: string): Promise<CitedReference[]> {
    return (await api.get(`/matters/${matterId}/references`)).data.references;
  },

  /**
   * Fetch the cited art.
   *
   * Enqueued and separate from reading the action, so a slow retrieval does not hold up
   * the rejection list. With no keys, only what has not been tried is fetched; naming
   * keys retries those.
   */
  async retrieveReferences(
    matterId: string,
    referenceKeys: string[] = []
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/references/retrieve`, { referenceKeys }))
      .data;
  },

  /**
   * The wrapper's non-patent literature, and what the last search made of it.
   *
   * Read from what the worker already wrote — the wrapper rows are on the matter and the
   * search's findings are on the matter too. Nothing is fetched or OCR'd to answer this.
   */
  async listNpl(
    matterId: string
  ): Promise<{
    documents: ProsecutionDocument[];
    citations: CitedReference[];
    search: NplSearch;
  }> {
    return (await api.get(`/matters/${matterId}/references/npl`)).data;
  },

  /**
   * Search the file wrapper for the non-patent literature the action cites.
   *
   * An examiner rejecting over a journal article files that article into the
   * application's own wrapper under USPTO document code NPL. There is no number to
   * retrieve it by, so the worker reads each candidate and matches it to the citation it
   * answers. Passing a `referenceKey` and `docId` attaches one by hand instead.
   *
   * Enqueued: an NPL reference is a scanned article and reading four of them is minutes.
   */
  async searchNpl(
    matterId: string,
    assignment?: { referenceKey: string; docId: string }
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/references/npl`, assignment ?? {})).data;
  },

  /**
   * Look for one non-patent reference on the web.
   *
   * The last resort, once the file wrapper has been searched and has not answered it —
   * non-patent literature is withheld from public Patent Center over copyright, so plenty
   * of what an examiner cites genuinely is not in the wrapper. The search is built from
   * what the action said: author, title, year.
   *
   * Enqueued, and the reference goes to "searching" as it is queued, so the panel's own
   * polling picks the result up without anything else being told about it.
   */
  async searchReferenceWeb(matterId: string, referenceKey: string): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/references/${referenceKey}/web`)).data;
  },

  /**
   * Add a reference the reading of the action missed.
   *
   * With a number it is retrieved straight away; with only a name it is recorded as
   * non-patent literature, to be found in the wrapper or on the web.
   */
  async addReference(
    matterId: string,
    input: { number?: string; label?: string; commonName?: string; issueIds?: string[] }
  ): Promise<CitedReference> {
    return (await api.post(`/matters/${matterId}/references`, input)).data.reference;
  },

  /** Take a reference off this matter — art the action does not actually rely on. */
  async removeReference(matterId: string, referenceKey: string): Promise<void> {
    await api.delete(`/matters/${matterId}/references/${referenceKey}`);
  },

  /**
   * Supply a reference the tool could not retrieve.
   *
   * Some art is simply not reachable — an NPL article, an old foreign application, a
   * number that resolves to nothing. The analysis says when it is working around a gap,
   * but being given the document beats being told about the gap.
   */
  async uploadReference(
    matterId: string,
    referenceKey: string,
    file: File
  ): Promise<{ reference: CitedReference; characters: number }> {
    const form = new FormData();
    form.append('file', file);
    return (
      await api.post(`/matters/${matterId}/references/${referenceKey}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      })
    ).data;
  },

  /**
   * One prosecution document from a cited application's file wrapper.
   *
   * Distinct from `downloadDocument`, which reads the wrapper of the application under
   * prosecution — a copending case cited in a double patenting rejection is a different
   * application, and reading the wrong wrapper returns the wrong document or none.
   */
  async downloadReferenceDocument(
    matterId: string,
    referenceKey: string,
    docId: string
  ): Promise<void> {
    await download(`/matters/${matterId}/references/${referenceKey}/documents/${docId}`);
  },

  /**
   * Rename a reference.
   *
   * The name the response uses throughout. Persisted on the reference, so the analysis,
   * the outline and the drafted argument all call it the same thing.
   */
  async renameReference(
    matterId: string,
    referenceKey: string,
    commonName: string
  ): Promise<CitedReference> {
    return (
      await api.patch(`/matters/${matterId}/references/${referenceKey}`, { commonName })
    ).data.reference;
  },

  /**
   * Correct a reference's number and fetch it again.
   *
   * Office actions carry typos, and a digit wrong in a citation retrieves a real and
   * unrelated document or nothing at all. The reference moves to a new key — it is keyed
   * by the number — and everything retrieved for the old document is left behind rather
   * than carried onto a different one. Retrieval starts on its own.
   */
  async correctReferenceNumber(
    matterId: string,
    referenceKey: string,
    number: string
  ): Promise<{ reference: CitedReference; mergedInto?: string }> {
    return (await api.patch(`/matters/${matterId}/references/${referenceKey}`, { number }))
      .data;
  },

  /** Discard an uploaded reference, putting it back to whatever retrieval found. */
  async removeReferenceUpload(
    matterId: string,
    referenceKey: string
  ): Promise<CitedReference> {
    return (await api.delete(`/matters/${matterId}/references/${referenceKey}/upload`))
      .data.reference;
  },

  // ---- analysing one rejection

  /**
   * Answer one rejection against the claims and the art it cites.
   *
   * The long call in this tool. The rejection goes to the model as the examiner wrote
   * it, with the claims as they now stand and the full text of each retrieved reference.
   */
  async analyzeIssue(
    matterId: string,
    issueId: string,
    input: { specification?: string; instructions?: string } = {}
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/issues/${issueId}/analyze`, input)).data;
  },

  async getIssueAnalysis(
    matterId: string,
    issueId: string
  ): Promise<IssueAnalysis | null> {
    return (await api.get(`/matters/${matterId}/issues/${issueId}/analysis`)).data.analysis;
  },

  // ---- step 4: the claims

  async generateClaims(
    matterId: string,
    input: { docId?: string; claimsText?: string; sourceKey?: string }
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/claims/generate`, input)).data;
  },

  async saveClaims(matterId: string, claims: ActionClaim[]): Promise<ActionClaim[]> {
    return (await api.put(`/matters/${matterId}/claims`, { claims })).data.claims;
  },

  /** Amend one claim, or change how it stands. A direct write, not a queued step. */
  async updateClaim(
    matterId: string,
    number: number,
    fields: { text?: string; amendedText?: string; status?: string }
  ): Promise<ActionClaim> {
    return (await api.patch(`/matters/${matterId}/claims/${number}`, fields)).data.claim;
  },

  // ---- amending the claims
  //
  // Before the arguments. An argument is written about the claims as they will be filed,
  // and outlining one against the claims of record produces a plan that has to be redone
  // the moment a limitation is added.

  /** Apply an amended claim set the attorney typed or pasted. */
  async amendClaims(matterId: string, amendedText: string): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/claims/amend`, { amendedText })).data;
  },

  /**
   * Apply an amended claim set from a file.
   *
   * A .docx is read from its own formatting and comes back at once — struck-through text
   * is a deletion and underlined text is an addition, which the file records as fact, so
   * there is nothing to infer and nothing to queue. Anything else returns a job handle
   * and is worked out asynchronously.
   */
  async amendClaimsFromFile(
    matterId: string,
    file: File
  ): Promise<{
    jobId?: string;
    amended?: number;
    unchanged?: number[];
    /** Untouched claims whose identifier the paper changed — withdrawn, previously presented. */
    restatused?: number[];
    canceled?: number[];
    new?: number[];
    source?: string;
    /** Markings that would draw an objection if filed as they stand. */
    warnings?: string[];
  }> {
    const form = new FormData();
    form.append('file', file);
    return (
      await api.post(`/matters/${matterId}/claims/amend/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      })
    ).data;
  },

  // ---- outlining the arguments

  /**
   * Plan the argument answering one rejection, or all of them.
   *
   * Naming an `issueId` plans that one alone. Without it every rejection is replanned —
   * which rewrites outlines already approved for the others, and rewriting an approved
   * plan un-approves it.
   */
  async generateOutline(
    matterId: string,
    input: { issueId?: string; specification?: string; instructions?: string } = {}
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/outline/generate`, input)).data;
  },

  /** Save an edited outline, or approve one. */
  async saveOutline(
    matterId: string,
    issueId: string,
    fields: Partial<ArgumentOutline>
  ): Promise<ArgumentOutline> {
    return (await api.put(`/matters/${matterId}/outline/${issueId}`, fields)).data.outline;
  },

  // ---- step 5: strategy

  async generateStrategy(
    matterId: string,
    input: { specification?: string; instructions?: string } = {}
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/strategy/generate`, input)).data;
  },

  // ---- step 6: the remarks

  /** Draft the answer to one issue, or to every issue when `issueId` is omitted. */
  async generateResponse(
    matterId: string,
    input: {
      issueId?: string;
      actionText?: string;
      specification?: string;
      references?: Record<string, string>;
      instructions?: string;
    } = {}
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/response/generate`, input)).data;
  },

  /**
   * Save the argument answering one rejection.
   *
   * Sending `sections` is preferred: the prose is composed from them server-side, so a
   * point switched off cannot survive in a stale `remarks` field and reach the paper.
   */
  async saveResponse(
    matterId: string,
    issueId: string,
    input: { remarks?: string; sections?: RemarkSection[]; amendmentNote?: string }
  ): Promise<IssueResponse> {
    return (await api.put(`/matters/${matterId}/response/${issueId}`, input)).data
      .response;
  },

  // ---- sections and assembly

  async saveSection(matterId: string, kind: string, text: string): Promise<void> {
    await api.put(`/matters/${matterId}/sections/${kind}`, { text });
  },

  /** The response as it will be filed, assembled on read. */
  async getShell(matterId: string): Promise<ResponseShell> {
    return (await api.get(`/matters/${matterId}/shell`)).data;
  },

  // ---- signature blocks

  /** The signature blocks this user has saved. */
  async listSignatures(): Promise<ActionSignature[]> {
    return (await api.get('/signatures')).data.signatures;
  },

  async createSignature(
    input: Partial<ActionSignature>
  ): Promise<ActionSignature> {
    return (await api.post('/signatures', input)).data.signature;
  },

  async updateSignature(
    signatureId: string,
    fields: Partial<ActionSignature>
  ): Promise<ActionSignature> {
    return (await api.patch(`/signatures/${signatureId}`, fields)).data.signature;
  },

  async deleteSignature(signatureId: string): Promise<void> {
    await api.delete(`/signatures/${signatureId}`);
  },

  // ---- the response shell, filled from a template

  /**
   * Read the prosecution and say what happened in it.
   *
   * Enqueued: every substantive paper in the wrapper is read — the scans among them
   * OCR'd — and one model call is made per exchange.
   */
  async generateTimeline(
    matterId: string,
    /** "update" reads only what is new; "full" reads the whole file again. */
    mode: 'update' | 'full' = 'update',
    limit = 0
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/timeline`, { mode, limit })).data;
  },

  async getTimeline(
    matterId: string
  ): Promise<{ timeline: ProsecutionTimeline | null; runAt: string }> {
    return (await api.get(`/matters/${matterId}/timeline`)).data;
  },

  /**
   * The same reading, for an application with no matter behind it.
   *
   * The USPTO Data page browses applications nobody has opened a response for, and the
   * question it answers is the same one.
   */
  async generateApplicationTimeline(
    applicationNumber: string,
    mode: 'update' | 'full' = 'update',
    limit = 0
  ): Promise<JobHandle> {
    return (
      await api.post(`/applications/${applicationNumber}/timeline`, { mode, limit })
    ).data;
  },

  async getApplicationTimeline(
    applicationNumber: string
  ): Promise<{ timeline: ProsecutionTimeline | null; runAt: string }> {
    return (await api.get(`/applications/${applicationNumber}/timeline`)).data;
  },

  /**
   * Fetch this application's specification, so the rejections can be answered against it.
   *
   * Enqueued: the published application is retrieved from the USPTO, and where it has not
   * published the specification paper in the file wrapper is OCR'd.
   */
  async fetchSpecification(
    matterId: string,
    choice: { source: 'publication' | 'wrapper'; docId?: string }
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/specification`, choice)).data;
  },

  /** Supply the specification by hand — for an application that has not published. */
  async uploadSpecification(matterId: string, file: File): Promise<ActionMatter> {
    const form = new FormData();
    form.append('file', file);
    return (
      await api.put(`/matters/${matterId}/specification`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      })
    ).data.matter;
  },

  /**
   * The specification as a document.
   *
   * The PDF where one can be had — the published application, the wrapper paper, or the
   * file that was uploaded — and the text otherwise. The text is also what `format=txt`
   * asks for outright: for a wrapper copy it is the OCR the arguments actually read, and
   * checking it against the scan is worth being able to do.
   */
  async downloadSpecification(
    matterId: string,
    format: 'pdf' | 'txt' = 'pdf'
  ): Promise<string> {
    const query = format === 'txt' ? '?format=txt' : '';
    return download(`/matters/${matterId}/specification/download${query}`);
  },

  /** Forget the stored specification, so another can be fetched or uploaded. */
  async clearSpecification(matterId: string): Promise<ActionMatter> {
    return (await api.delete(`/matters/${matterId}/specification`)).data.matter;
  },

  /**
   * The template library: every template, what each one can hold, and what this matter
   * needs that it does not.
   *
   * `matterId` is what makes it useful — a template with no `{{112_rejections}}` is the
   * wrong template for an action raising one, and that is worth knowing before the
   * document is generated rather than after.
   */
  async templateLibrary(matterId?: string): Promise<TemplateLibrary> {
    const query = matterId ? `?matterId=${encodeURIComponent(matterId)}` : '';
    return (await api.get(`/templates${query}`)).data;
  },

  /** Add a response template. Read in the worker, so this returns a job. */
  async uploadTemplate(
    file: File,
    options: { name?: string; autoDetect?: boolean } = {}
  ): Promise<JobHandle> {
    const form = new FormData();
    form.append('file', file);
    if (options.name) form.append('name', options.name);
    form.append('autoDetect', String(options.autoDetect ?? true));
    return (
      await api.post('/templates', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      })
    ).data;
  },

  /**
   * The template document itself, to be edited in Word.
   *
   * With its placeholders in place, which is the point: a firm adjusting the shell needs
   * to see where `{{103_rejections}}` sits before moving anything around it, and a copy
   * with the markers stripped could not be uploaded back.
   */
  async downloadTemplate(templateId: string, name?: string): Promise<void> {
    await download(`/templates/${templateId}/download`, name);
  },

  /**
   * Put an edited document in place of this template, keeping its identity.
   *
   * The template keeps its id, so every matter naming it goes on naming it — replacing a
   * shell is editing it, not making a second one. The placeholders are read again from
   * the new document, so this returns a job.
   */
  async replaceTemplate(templateId: string, file: File): Promise<JobHandle> {
    const form = new FormData();
    form.append('file', file);
    return (
      await api.put(`/templates/${templateId}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      })
    ).data;
  },

  /** Rename a template, or say what kind of response it is for. */
  async updateTemplate(
    templateId: string,
    changes: {
      name?: string;
      responseType?: string;
      /** Per-section traverse sentences. Sent whole; the server replaces the set. */
      traversals?: Record<string, { enabled: boolean; sentence: string }>;
    }
  ): Promise<ResponseTemplate> {
    return (await api.patch(`/templates/${templateId}`, changes)).data.template;
  },

  async deleteTemplate(templateId: string): Promise<void> {
    await api.delete(`/templates/${templateId}`);
  },

  /** Just the templates, for a picker that does not need the coverage detail. */
  async listTemplates(matterId?: string): Promise<ResponseTemplate[]> {
    return (await actionApi.templateLibrary(matterId)).templates;
  },

  /**
   * Fill a template from this matter's Office action and claims.
   *
   * Enqueued — it reads both documents and writes a .docx. The matter's chosen action is
   * used unless one is named, so the attorney picks it once rather than again here.
   */
  async generateShellDocument(
    matterId: string,
    input: {
      templateId: string;
      templateName?: string;
      docId?: string;
      claimsDocId?: string;
      /** Which saved block to fill in under the closing. The default is used if omitted,
       *  and the signature line is left empty either way. */
      signatureId?: string;
      supplementalDocIds?: string[];
      /** Show the amendment as Word tracked changes instead of the struck-through,
       *  underlined and bold markup the filed paper uses. */
      trackChanges?: boolean;
    }
  ): Promise<JobHandle> {
    return (await api.post(`/matters/${matterId}/shell/generate`, input)).data;
  },

  /**
   * The finished response: the firm's template, filled from the work that was done.
   *
   * Distinct from `generateShellDocument`. That one is generated at the claims step,
   * before anything has been argued, so a model writes its sections from the Office
   * action. This one puts the approved remarks, the amended claims and the allowable
   * subject matter into the paper — nothing is written again.
   */
  async generateResponseDocument(
    matterId: string,
    input: {
      templateId: string;
      templateName?: string;
      signatureId?: string;
      /** File with a rejection left unanswered. Refused without it. */
      allowUnanswered?: boolean;
      /** Show the amendment as Word tracked changes instead of the struck-through,
       *  underlined and bold markup the filed paper uses. */
      trackChanges?: boolean;
    }
  ): Promise<{
    jobId: string;
    /** Arguments this template has no section for — placed by hand or not at all. */
    unplaced?: string[];
    unanswered?: string[];
  }> {
    return (await api.post(`/matters/${matterId}/response/document`, input)).data;
  },

  /**
   * Filled documents produced for this matter, newest first.
   *
   * `kind` scopes it to one of the two: the claims step wants its shells and the assemble
   * step its responses. Both are produced by the same filler into the same table, so
   * without it each step listed the other's work as well. Documents generated before the
   * distinction was recorded carry no kind and are returned for either.
   */
  async listShellDocuments(
    matterId: string,
    kind?: 'shell' | 'response'
  ): Promise<FilledShell[]> {
    const query = kind ? `?kind=${kind}` : '';
    return (await api.get(`/matters/${matterId}/shell/documents${query}`)).data.documents;
  },

  /**
   * Download one filled shell as a .docx.
   *
   * Streamed through the backend rather than fetched from a presigned URL, so ownership
   * is confirmed before the bytes are handed over.
   */
  async downloadShellDocument(
    matterId: string,
    filledId: string,
    filename: string
  ): Promise<void> {
    const response = await api.get(
      `/matters/${matterId}/shell/documents/${encodeURIComponent(filledId)}`,
      { responseType: 'blob', timeout: 120000 }
    );
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.toLowerCase().endsWith('.docx')
      ? filename
      : `${filename}.docx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoking immediately can cancel the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  /** Status of one generation step. `status` is the word; `error` is why, if it failed. */
  async getJob(
    jobId: string
  ): Promise<{ jobId: string; status: string; error?: string }> {
    return (await api.get(`/jobs/${jobId}`)).data;
  },
};

/**
 * Fetch a file and hand it to the browser.
 *
 * Streamed through the API rather than opened as a URL, so the Bearer token travels with
 * it and ownership is confirmed before any bytes are sent. The filename comes from the
 * response's Content-Disposition where the server set one — it knows the application
 * number and the document code, and the client does not.
 */
async function download(path: string, fallbackName?: string): Promise<string> {
  const response = await api.get(path, { responseType: 'blob', timeout: 180000 });

  // The server names the file — it knows the reference, the document code and the date.
  // This is only the fallback for when that header cannot be read, and it has to strip
  // the query string: the last segment of "/references/x/download?format=pdf" is not a
  // filename, and using it produced downloads called "download".
  const segment = (path.split('?')[0].split('/').pop() || '').trim();
  let filename =
    fallbackName || (segment.includes('.') ? segment : '') || 'document';
  const disposition = String(response.headers?.['content-disposition'] || '');
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (match) filename = decodeURIComponent(match[1]);

  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  // What actually landed. A route that offers a PDF and falls back to the text names the
  // file honestly, and the caller can say so rather than leaving the reader to notice.
  return filename;
}

/**
 * A stored timestamp, in the reader's own time zone.
 *
 * The services write UTC, and until recently wrote it without an offset. JavaScript reads
 * a bare ISO date-time as *local*, so a shell generated at 6pm Eastern came back labelled
 * 10pm — the UTC clock time, shown as though it were local. A timestamp with no zone is
 * therefore read as UTC, which is what it is.
 */
export function localTime(stamp?: string): string {
  if (!stamp) return '';
  const zoned = /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(stamp) ? stamp : `${stamp}Z`;
  const date = new Date(zoned);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

/**
 * Human-readable message from a failure, preferring the backend's own text.
 *
 * Falls back to a plain `Error`'s message before the generic line. This code throws
 * `Error` with something specific to say — "that is taking longer than expected", "the
 * message could not be written" — and reading only the axios shape replaced every one of
 * them with "Something went wrong", which is the least useful thing the app could say
 * and was hiding the real cause.
 */
/**
 * Where this account stands against its monthly ceiling for this tool — how many it
 * may still start, and when the count resets. The create route refuses with 429
 * `monthly_limit` once it is reached; the message on that reply says what to do.
 */
export interface MonthlyQuota {
  tool: 'drafting' | 'action';
  accountType: string;
  month: string;
  used: number;
  limit: number;
  planLimit: number;
  override: number | null;
  remaining: number;
  resetsAt: string;
}

export function actionError(error: unknown): string {
  const response = (error as { response?: { data?: { message?: string; error?: string } } })
    ?.response;
  const own = response?.data?.message || response?.data?.error;
  if (own) return own;

  const message = (error as { message?: string })?.message;
  // Axios's own network text says nothing a reader can act on; the generic line at least
  // suggests trying again.
  if (message && !/^(Network Error|Request failed with status)/i.test(message)) {
    return message;
  }
  return 'Something went wrong. Please try again.';
}

/** The strategies the backend accepts, for a picker that cannot go out of step with it. */
export const STRATEGIES: Strategy[] = [
  'argue',
  'amend',
  'argue_and_amend',
  'accept',
  'interview',
  'undecided',
];
